// Hook para conversão de moedas com atualização automática

import { useState, useCallback, useEffect, useRef } from 'react';
import { FEE_PERCENT, FEE_BILLING_PERCENT, FEE_MIN_AMOUNT, IS_FEE_ENABLED } from '@/config/feeConfig';

const FALLBACK_RATES = {
  'USD': 1,
  'BRL': 5.15,
};

const UPDATE_INTERVAL = 60000; // 60 segundos

export function useCurrencyConversion() {
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [nextUpdate, setNextUpdate] = useState(UPDATE_INTERVAL);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);

  // Buscar taxas de câmbio da API (Awesome API - brasileira, sem CORS)
  const fetchRates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Awesome API - API brasileira gratuita, sem necessidade de key
      const response = await fetch('https://economia.awesomeapi.com.br/last/USD-BRL');
      
      if (!response.ok) {
        throw new Error('Erro ao buscar cotação');
      }
      
      const data = await response.json();
      
      // Usar média entre bid (compra) e ask (venda) para maior precisão
      const bid = parseFloat(data.USDBRL?.bid) || FALLBACK_RATES.BRL;
      const ask = parseFloat(data.USDBRL?.ask) || FALLBACK_RATES.BRL;
      const midRate = (bid + ask) / 2;
      
      const newRates = {
        'USD': 1,
        'BRL': parseFloat(midRate.toFixed(4)),
      };
      
      setRates(newRates);
      setLastUpdate(new Date());
      setNextUpdate(UPDATE_INTERVAL);
      
      return newRates;
    } catch (err) {
      console.error('[Currency] Erro ao buscar taxas:', err.message);
      setError(err.message);
      // Manter taxas anteriores em caso de erro
      return rates;
    } finally {
      setIsLoading(false);
    }
  }, [rates]);

  // Iniciar timer de atualização
  useEffect(() => {
    // Buscar taxas iniciais
    fetchRates();

    // Configurar intervalo de atualização
    timerRef.current = setInterval(() => {
      fetchRates();
    }, UPDATE_INTERVAL);

    // Configurar countdown
    countdownRef.current = setInterval(() => {
      setNextUpdate(prev => {
        if (prev <= 1000) return UPDATE_INTERVAL;
        return prev - 1000;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  // Converter entre moedas
  const convert = useCallback((amount, fromCurrency, toCurrency) => {
    if (!amount || isNaN(amount)) return 0;
    
    const numAmount = parseFloat(amount);
    
    if (fromCurrency === toCurrency) return numAmount;
    
    if (fromCurrency === 'USD' && toCurrency === 'BRL') {
      return parseFloat((numAmount * rates.BRL).toFixed(2));
    }
    
    if (fromCurrency === 'BRL' && toCurrency === 'USD') {
      return parseFloat((numAmount / rates.BRL).toFixed(2));
    }
    
    // Conversão genérica via USD
    const amountInUSD = numAmount / (rates[fromCurrency] || 1);
    const result = amountInUSD * (rates[toCurrency] || 1);
    return parseFloat(result.toFixed(2));
  }, [rates]);

  // Calcular taxa de transferência por percentual (valor mínimo é só para permitir envio/recebimento, não afeta o cálculo)
  const calculateFee = useCallback((amount, overridePercentage = null) => {
    if (!amount || isNaN(amount)) return 0;
    const numAmount = parseFloat(amount);
    if (!IS_FEE_ENABLED) return 0;
    const percentage = overridePercentage !== null ? overridePercentage : FEE_PERCENT;
    return parseFloat((numAmount * (percentage / 100)).toFixed(2));
  }, []);

  // Segundos restantes para próxima atualização
  const secondsUntilUpdate = Math.ceil(nextUpdate / 1000);

  // Progresso do timer (0-100)
  const timerProgress = ((UPDATE_INTERVAL - nextUpdate) / UPDATE_INTERVAL) * 100;

  // Calcula taxa de recebimento (serviço) por percentual
  const calculateBillingFee = useCallback((amount) => {
    if (!amount || isNaN(amount)) return 0;
    const numAmount = parseFloat(amount);
    if (!IS_FEE_ENABLED || FEE_BILLING_PERCENT <= 0) return 0;
    return parseFloat((numAmount * (FEE_BILLING_PERCENT / 100)).toFixed(2));
  }, []);

  return {
    rates,
    convert,
    calculateFee,
    calculateBillingFee,
    fetchRates,
    lastUpdate,
    secondsUntilUpdate,
    timerProgress,
    isLoading,
    error,
    // Configurações de taxa e valor mínimo para envio/recebimento (USD)
    feePercent: FEE_PERCENT,
    feeBillingPercent: FEE_BILLING_PERCENT,
    feeEnabled: IS_FEE_ENABLED,
    minSendChargeAmount: FEE_MIN_AMOUNT,
  };
}
